/**
 * The one code path behind every programmatic citation request:
 * POST /api/v1/cite and the MCP `generate_citation` tool both call `runCite`,
 * so an agent gets identical output whichever door it comes through.
 *
 * This is a thin orchestration layer over the protected formatter core in
 * `src/lib/citation.ts` — it resolves the style slug, fans out over a batch,
 * and turns per-item failures into per-item errors instead of failing the
 * whole request. It does not format anything itself.
 */
import { getCitationStyleBySlug } from "@/data/citation-styles";
import { getStyleLandingPage } from "@/data/style-pages";
import {
  type CitationClusterItem,
  type DocumentBibliographyFormat,
  type DocumentCitation,
  type DocumentSource,
  type EditableEntry,
  formatCitation,
  formatDocumentCitations,
  formatFromEntry,
  formatInText,
  formatInTextCluster,
  isAllowedStyle,
  normalizeLocator,
} from "@/lib/citation";
import {
  MAX_BATCH,
  MAX_DOCUMENT_CITATION_ITEMS,
  MAX_DOCUMENT_CITATIONS,
  MAX_DOCUMENT_CITED_ITEMS,
  MAX_DOCUMENT_ENTRY_LENGTH,
  MAX_DOCUMENT_SOURCES,
} from "@/lib/cite-limits";
import { normalizeOutputLocale } from "@/lib/output-locales";
import { htmlToRuns, runsToText, type TextRun } from "@/lib/rich-text-runs";
import { getStyleExample } from "@/lib/style-examples";

/** Longest accepted caller-chosen source id. */
const MAX_SOURCE_ID_LENGTH = 64;

// biome-ignore lint/performance/noBarrelFile: existing importers read MAX_BATCH from here.
export {
  MAX_BATCH,
  MAX_DOCUMENT_CITATION_ITEMS,
  MAX_DOCUMENT_CITATIONS,
  MAX_DOCUMENT_SOURCES,
} from "@/lib/cite-limits";

/** Longest accepted input string, matching the existing /api/cite guard. */
export const MAX_INPUT_LENGTH = 2000;

export interface ResolvedStyle {
  /** Slug the caller asked for. */
  requested: string;
  /** Slug the CSL engine formats with. */
  cslSlug: string;
  /** Fetch path for dependent styles; undefined for root styles. */
  cslPath: string | undefined;
  /** Display name of the style the caller asked for. */
  name: string;
  /**
   * Set when the requested style is a *dependent* CSL style and formatting came
   * from the independent parent it points at. Null for independent styles.
   */
  independentParent: string | null;
}

/**
 * A dependent CSL style contains no formatting of its own — it is a pointer at
 * an independent parent (`<link rel="independent-parent">`). Roughly 7,600 of
 * the 10,834 styles are dependent, so handing one straight to citeproc fails.
 * Walk to the parent instead; the hop limit guards against a data cycle.
 */
const MAX_PARENT_HOPS = 4;

function followIndependentParent(slug: string): {
  slug: string;
  cslPath: string | undefined;
  parent: string | null;
} {
  let current = getCitationStyleBySlug(slug);
  let parent: string | null = null;
  for (let hop = 0; hop < MAX_PARENT_HOPS; hop++) {
    const parentSlug = getStyleExample(current?.slug ?? slug)?.p;
    if (!parentSlug) {
      break;
    }
    const parentMeta = getCitationStyleBySlug(parentSlug);
    if (!parentMeta) {
      break;
    }
    parent = parentMeta.slug;
    current = parentMeta;
  }
  return {
    slug: current?.slug ?? slug,
    cslPath: current?.cslPath,
    parent,
  };
}

/**
 * Resolve a caller-supplied style slug.
 *
 * Accepts both catalog slugs (`chicago-author-date`, `nature`) and the friendly
 * landing-page slugs people actually type (`chicago`, `harvard`), because an
 * agent asked for "Chicago" should not have to know which Chicago variant the
 * CSL repository filename uses.
 */
export function resolveStyle(slug: string): ResolvedStyle | null {
  const trimmed = slug.trim();
  if (!trimmed) {
    return null;
  }
  const direct = getCitationStyleBySlug(trimmed);
  if (direct) {
    const target = followIndependentParent(direct.slug);
    return {
      requested: trimmed,
      cslSlug: target.slug,
      cslPath: target.cslPath,
      name: direct.name,
      independentParent: target.parent,
    };
  }
  const landing = getStyleLandingPage(trimmed);
  if (landing) {
    const target = followIndependentParent(landing.cslSlug);
    return {
      requested: trimmed,
      cslSlug: target.slug,
      cslPath: target.cslPath,
      name: landing.fullName,
      independentParent: target.parent,
    };
  }
  // Unknown to the catalog but a syntactically valid CSL slug: let it through so
  // newly published CSL styles work before the catalog is regenerated.
  if (isAllowedStyle(trimmed)) {
    return {
      requested: trimmed,
      cslSlug: trimmed,
      cslPath: undefined,
      name: trimmed,
      independentParent: null,
    };
  }
  return null;
}

export interface CiteRequest {
  /** One source: URL, DOI, ISBN, PMID, arXiv ID, or BibTeX/RIS/CSL-JSON text. */
  input?: string;
  /** Up to MAX_BATCH sources, formatted in one call. */
  inputs?: string[];
  /** A CSL-JSON-shaped entry to format directly, skipping lookup. */
  entry?: EditableEntry;
  /**
   * Entries to render together as one in-text citation cluster. Takes precedence over
   * the single-source forms and may include a CSL locator on each source.
   */
  cluster?: CitationClusterItem[];
  /**
   * A whole document: every source it cites and every citation in reading order,
   * rendered in one citeproc run so numbering, disambiguation, and repeat-note forms
   * are right. Takes precedence over every other source form.
   */
  document?: {
    sources: DocumentSource[];
    citations: DocumentCitation[];
  };
  /** Style slug. Defaults to APA 7. */
  style?: string;
  /** Output language for CSL terms; unknown values fall back to en-US. */
  lang?: unknown;
  /** Also return the in-text citation. */
  inText?: boolean;
  /** Also return the full footnote (note-based styles). */
  note?: boolean;
}

export interface CiteItemResult {
  /** The input this result came from; null when an `entry` object was supplied. */
  input: string | null;
  citation: string | null;
  inText?: string | null;
  note?: string | null;
  entry: EditableEntry | null;
  error?: string;
}

export interface CiteResponse {
  style: {
    /** Exactly what the caller sent. */
    requested: string;
    /** The style the engine formatted with. */
    slug: string;
    /** Display name of the requested style. */
    name: string;
    /** The parent formatting came from, when the requested style is dependent. */
    independentParent: string | null;
  };
  language: string;
  results: CiteItemResult[];
  /** Present only for a `cluster` request; produced by one citeproc citation call. */
  cluster?: string;
  /** Present only for a `document` request. */
  document?: DocumentResult;
}

/** Formatted text in two forms: plain, and styled runs that keep italics and caps. */
export interface RichText {
  text: string;
  runs: TextRun[];
}

export interface DocumentResult {
  /** "note" means each citation is footnote text; "in-text" means it goes inline. */
  styleClass: "in-text" | "note";
  /** One per supplied citation, in the same order. */
  citations: RichText[];
  /** The reference list, in the style's own order. */
  bibliography: (RichText & { id: string })[];
  bibliographyFormat: DocumentBibliographyFormat;
}

export class CiteRequestError extends Error {
  status: number;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    status = 400,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "CiteRequestError";
    this.status = status;
    this.details = details;
  }
}

interface Extras {
  inText: boolean;
  note: boolean;
}

/** Compute the optional extra renders for an already-parsed entry. */
async function buildExtras(
  entry: EditableEntry,
  style: ResolvedStyle,
  locale: string,
  extras: Extras
): Promise<Pick<CiteItemResult, "inText" | "note">> {
  const out: Pick<CiteItemResult, "inText" | "note"> = {};
  if (extras.inText) {
    const { inText } = await formatInText(
      entry,
      style.cslSlug,
      style.cslPath,
      locale
    );
    out.inText = inText;
  }
  if (extras.note) {
    const { citation } = await formatFromEntry(
      entry,
      style.cslSlug,
      style.cslPath,
      locale,
      "note"
    );
    out.note = citation;
  }
  return out;
}

async function citeOneInput(
  input: string,
  style: ResolvedStyle,
  locale: string,
  extras: Extras
): Promise<CiteItemResult> {
  try {
    const { citation, entry } = await formatCitation(
      input,
      style.cslSlug,
      style.cslPath,
      locale
    );
    const extra = entry ? await buildExtras(entry, style, locale, extras) : {};
    return { input, citation, entry, ...extra };
  } catch (err) {
    return {
      input,
      citation: null,
      entry: null,
      error: err instanceof Error ? err.message : "Failed to format citation.",
    };
  }
}

async function citeEntryObject(
  entry: EditableEntry,
  style: ResolvedStyle,
  locale: string,
  extras: Extras
): Promise<CiteItemResult> {
  const { citation } = await formatFromEntry(
    entry,
    style.cslSlug,
    style.cslPath,
    locale
  );
  const extra = await buildExtras(entry, style, locale, extras);
  return { input: null, citation, entry, ...extra };
}

/** Normalize `input`/`inputs` into a validated list, or throw a 400. */
function readInputs(request: CiteRequest): string[] {
  const raw = request.inputs ?? (request.input ? [request.input] : []);
  const inputs = raw
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);
  if (inputs.length === 0) {
    throw new CiteRequestError(
      "Provide `input` (a URL, DOI, ISBN, PMID, arXiv ID, or BibTeX/RIS/CSL-JSON string), `inputs` (an array of them), or an `entry` object.",
      400
    );
  }
  if (inputs.length > MAX_BATCH) {
    throw new CiteRequestError(
      `Too many inputs: ${inputs.length}. Maximum is ${MAX_BATCH} per request.`,
      400
    );
  }
  const tooLong = inputs.find((value) => value.length > MAX_INPUT_LENGTH);
  if (tooLong) {
    throw new CiteRequestError(
      `Input exceeds ${MAX_INPUT_LENGTH} characters.`,
      400
    );
  }
  return inputs;
}

function isEntryObject(value: unknown): value is EditableEntry {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Strictly validate the additive cluster request shape. */
function readCluster(value: unknown): CitationClusterItem[] | null {
  if (value === undefined) {
    return null;
  }
  if (!Array.isArray(value) || value.length === 0) {
    throw new CiteRequestError(
      "`cluster` must be a non-empty array of citation items.",
      400
    );
  }
  if (value.length > MAX_BATCH) {
    throw new CiteRequestError(
      `Too many cluster items: ${value.length}. Maximum is ${MAX_BATCH} per request.`,
      400
    );
  }

  return value.map((item, index) => {
    if (!(item && typeof item === "object" && !Array.isArray(item))) {
      throw new CiteRequestError(
        `Cluster item ${index + 1} must be an object.`,
        400
      );
    }
    const candidate = item as { entry?: unknown; locator?: unknown };
    if (!isEntryObject(candidate.entry)) {
      throw new CiteRequestError(
        `Cluster item ${index + 1} must include an \`entry\` object.`,
        400
      );
    }
    if (candidate.locator === undefined || candidate.locator === null) {
      return { entry: candidate.entry };
    }
    const locator = normalizeLocator(candidate.locator);
    if (!locator) {
      throw new CiteRequestError(
        `Cluster item ${index + 1} has an invalid locator.`,
        400
      );
    }
    return { entry: candidate.entry, locator };
  });
}

function toRichText(html: string): RichText {
  const runs = htmlToRuns(html);
  return { text: runsToText(runs), runs };
}

function readSourceId(value: unknown, where: string): string {
  if (
    typeof value !== "string" ||
    !value.trim() ||
    value.length > MAX_SOURCE_ID_LENGTH
  ) {
    throw new CiteRequestError(
      `${where} needs an \`id\` string of 1 to ${MAX_SOURCE_ID_LENGTH} characters.`,
      400
    );
  }
  return value;
}

function readDocumentSources(value: unknown): DocumentSource[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new CiteRequestError(
      "`document.sources` must be a non-empty array of { id, entry }.",
      400
    );
  }
  if (value.length > MAX_DOCUMENT_SOURCES) {
    throw new CiteRequestError(
      `Too many document sources: ${value.length}. Maximum is ${MAX_DOCUMENT_SOURCES}.`,
      400
    );
  }
  const seen = new Set<string>();
  return value.map((source, index) => {
    const where = `Document source ${index + 1}`;
    const candidate = (source ?? {}) as { id?: unknown; entry?: unknown };
    const id = readSourceId(candidate.id, where);
    if (seen.has(id)) {
      throw new CiteRequestError(`Duplicate document source id "${id}".`, 400);
    }
    seen.add(id);
    if (!isEntryObject(candidate.entry)) {
      throw new CiteRequestError(
        `${where} must include an \`entry\` object.`,
        400
      );
    }
    if (JSON.stringify(candidate.entry).length > MAX_DOCUMENT_ENTRY_LENGTH) {
      throw new CiteRequestError(
        `${where} is larger than ${MAX_DOCUMENT_ENTRY_LENGTH} characters.`,
        400
      );
    }
    return { id, entry: candidate.entry };
  });
}

function readDocumentCitation(
  value: unknown,
  index: number,
  sourceIds: Set<string>
): DocumentCitation {
  const where = `Document citation ${index + 1}`;
  const candidate = (value ?? {}) as { items?: unknown; noteIndex?: unknown };
  const { items } = candidate;
  if (!Array.isArray(items) || items.length === 0) {
    throw new CiteRequestError(
      `${where} needs a non-empty \`items\` array.`,
      400
    );
  }
  if (items.length > MAX_DOCUMENT_CITATION_ITEMS) {
    throw new CiteRequestError(
      `${where} cites ${items.length} sources. Maximum is ${MAX_DOCUMENT_CITATION_ITEMS}.`,
      400
    );
  }
  const citation: DocumentCitation = {
    items: items.map((item) => {
      const cite = (item ?? {}) as { id?: unknown; locator?: unknown };
      const id = readSourceId(cite.id, where);
      if (!sourceIds.has(id)) {
        throw new CiteRequestError(
          `${where} cites unknown source "${id}".`,
          400
        );
      }
      if (cite.locator === undefined || cite.locator === null) {
        return { id };
      }
      const locator = normalizeLocator(cite.locator);
      if (!locator) {
        throw new CiteRequestError(`${where} has an invalid locator.`, 400);
      }
      return { id, locator };
    }),
  };
  if (candidate.noteIndex !== undefined) {
    const noteIndex = candidate.noteIndex;
    if (!(Number.isInteger(noteIndex) && (noteIndex as number) >= 0)) {
      throw new CiteRequestError(
        `${where} has an invalid \`noteIndex\`; use a footnote number, or 0 for in-text.`,
        400
      );
    }
    citation.noteIndex = noteIndex as number;
  }
  return citation;
}

/** Strictly validate the additive `document` request shape. */
function readDocument(
  value: unknown
): { sources: DocumentSource[]; citations: DocumentCitation[] } | null {
  if (value === undefined) {
    return null;
  }
  if (!isEntryObject(value)) {
    throw new CiteRequestError(
      "`document` must be an object with `sources` and `citations`.",
      400
    );
  }
  const { sources: rawSources, citations: rawCitations } = value as {
    sources?: unknown;
    citations?: unknown;
  };
  const sources = readDocumentSources(rawSources);
  if (!Array.isArray(rawCitations) || rawCitations.length === 0) {
    throw new CiteRequestError(
      "`document.citations` must be a non-empty array of { items }.",
      400
    );
  }
  if (rawCitations.length > MAX_DOCUMENT_CITATIONS) {
    throw new CiteRequestError(
      `Too many document citations: ${rawCitations.length}. Maximum is ${MAX_DOCUMENT_CITATIONS}.`,
      400
    );
  }
  const sourceIds = new Set(sources.map((source) => source.id));
  const citations = rawCitations.map((citation, index) =>
    readDocumentCitation(citation, index, sourceIds)
  );
  const citedItems = citations.reduce(
    (total, citation) => total + citation.items.length,
    0
  );
  if (citedItems > MAX_DOCUMENT_CITED_ITEMS) {
    throw new CiteRequestError(
      `Too many cited sources across the document: ${citedItems}. Maximum is ${MAX_DOCUMENT_CITED_ITEMS}.`,
      400
    );
  }
  return { sources, citations };
}

async function citeDocument(
  document: { sources: DocumentSource[]; citations: DocumentCitation[] },
  style: ResolvedStyle,
  locale: string
): Promise<DocumentResult> {
  const formatted = await formatDocumentCitations(
    document.sources,
    document.citations,
    style.cslSlug,
    style.cslPath,
    locale
  );
  return {
    styleClass: formatted.styleClass,
    citations: formatted.citations.map(toRichText),
    bibliography: formatted.bibliography.map((entry) => ({
      id: entry.id,
      ...toRichText(entry.html),
    })),
    bibliographyFormat: formatted.bibliographyFormat,
  };
}

/**
 * Format one or more sources. Throws `CiteRequestError` for bad requests;
 * per-source failures (a dead DOI, an unreachable URL) come back as `error` on
 * that item so a batch never fails as a whole.
 */
export async function runCite(request: CiteRequest): Promise<CiteResponse> {
  const style = resolveStyle(request.style ?? "apa");
  if (!style) {
    throw new CiteRequestError(
      `Unknown or invalid citation style "${request.style}".`,
      400,
      { hint: "Search styles at /api/v1/styles?q=<name>." }
    );
  }
  const locale = normalizeOutputLocale(request.lang);
  const extras: Extras = {
    inText: request.inText === true,
    note: request.note === true,
  };
  const styleInfo = {
    requested: style.requested,
    slug: style.cslSlug,
    name: style.name,
    // Dependent styles carry no formatting of their own; say so rather than
    // letting the caller think `slug` is what they asked for.
    independentParent: style.independentParent,
  };

  const document = readDocument(request.document);
  if (document) {
    return {
      style: styleInfo,
      language: locale,
      results: [],
      document: await citeDocument(document, style, locale),
    };
  }

  const cluster = readCluster(request.cluster);
  if (cluster) {
    const { inText } = await formatInTextCluster(
      cluster,
      style.cslSlug,
      style.cslPath,
      locale
    );
    const results: CiteItemResult[] = [];
    for (const item of cluster) {
      results.push(
        await citeEntryObject(item.entry, style, locale, {
          inText: false,
          note: false,
        })
      );
    }
    return {
      style: styleInfo,
      language: locale,
      results,
      cluster: inText,
    };
  }

  if (isEntryObject(request.entry)) {
    const result = await citeEntryObject(request.entry, style, locale, extras);
    return { style: styleInfo, language: locale, results: [result] };
  }

  const inputs = readInputs(request);
  // Sequential on purpose: each identifier is an upstream lookup (Crossref,
  // Open Library, PubMed) and those APIs rate-limit parallel bursts.
  const results: CiteItemResult[] = [];
  for (const input of inputs) {
    results.push(await citeOneInput(input, style, locale, extras));
  }
  return { style: styleInfo, language: locale, results };
}
